import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-hot-toast";

const initialStatePermission = {
    permissions: [],
    rolePermissions: {},
    success: false,
    message: '',
    loading: false,
};

const handleErrors = (error, dispatch, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

export const fetchPermissions = createAsyncThunk(
    "permission/fetchPermissions",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/permissions`);
            return response.data.permissions;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const fetchRolePermissions = createAsyncThunk(
    "permission/fetchRolePermissions",
    async (role, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/permissions/${role}`);
            return response.data.permission;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const updateRolePermissions = createAsyncThunk(
    "permission/updateRolePermissions",
    async (data, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/permissions`, data);
            toast.success(response.data.message || "Permissions updated successfully");
            return response.data.permission;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to update permissions");
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

const permissionSlice = createSlice({
    name: 'permission',
    initialState: initialStatePermission,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchPermissions.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchPermissions.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.permissions = action.payload;
            })
            .addCase(fetchPermissions.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch permissions';
            })
            .addCase(fetchRolePermissions.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchRolePermissions.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.rolePermissions = {
                    ...state.rolePermissions,
                    [action.payload.role]: action.payload.allowedPages
                };
            })
            .addCase(fetchRolePermissions.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch role permissions';
            })
            .addCase(updateRolePermissions.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateRolePermissions.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.rolePermissions = {
                    ...state.rolePermissions,
                    [action.payload.role]: action.payload.allowedPages
                };
                // Also update permissions list
                state.permissions = state.permissions.map(p => 
                    p.role === action.payload.role ? action.payload : p
                );
            })
            .addCase(updateRolePermissions.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to update permissions';
            });
    },
});

export default permissionSlice.reducer;
